import React from "react";
import { useQuery } from "@tanstack/react-query";
import { StatCard } from "../components/shared/StatCard";
import { Toggle } from "../components/shared/Toggle";
import { Spinner } from "../components/shared/Spinner";
import { paymentsAPI } from "../services/api";
import { fmt } from "../components/shared/fmt";
import { useTitle } from "../hooks/useTitle";

export const PaymentsPage: React.FC = () => {
	useTitle("Payments");
	const { data: mData, isLoading: ml } = useQuery({
		queryKey: ["payMethods"],
		queryFn: paymentsAPI.getMethods,
	});
	const { data: sData } = useQuery({
		queryKey: ["paySum"],
		queryFn: () => paymentsAPI.getSummary(),
	});
	const methods = mData?.data || [];
	const summary = sData?.data || {};
	const bm = summary.byMethod || {};
	const totalRev = Object.values(bm).reduce(
		(s: number, v: any) => s + (v.amount || 0),
		0,
	);

	if (ml) return <Spinner />;

	return (
		<div>
			<div className="page-title">Payment Settings</div>
			<div className="page-sub">
				Payment methods configuration and today's transaction breakdown
			</div>

			<div className="stat-grid">
				<StatCard
					label="Today's Revenue"
					value={fmt(summary.totalRevenue || 0)}
					cls="green"
					icon="💰"
					accent="#06a384"
				/>
				<StatCard
					label="Transactions"
					value={summary.totalTransactions || 0}
					icon="📊"
				/>
				<StatCard
					label="Total Tax"
					value={fmt(summary.totalTax || 0)}
					cls="amber"
					icon="🏛️"
				/>
				<StatCard
					label="Active Orders"
					value={summary.activeOrders || 0}
					cls="amber"
					icon="🔓"
				/>
				<StatCard
					label="Active Methods"
					value={`${methods.filter((m: any) => m.active).length}/${methods.length}`}
					icon="💳"
				/>
			</div>

			<div className="card">
				<div className="card-title">Payment Methods</div>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))",
						gap: 12,
					}}>
					{methods.map((m: any) => {
						const md = bm[m.id] || { amount: 0, count: 0 };
						const share =
							totalRev > 0 ? ((md.amount / totalRev) * 100).toFixed(1) : "0";
						return (
							<div
								key={m.id}
								style={{
									background: "var(--card2)",
									borderRadius: "var(--r)",
									border: `1.5px solid ${m.active ? `${m.color}33` : "var(--border)"}`,
									padding: 18,
								}}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										marginBottom: 12,
									}}>
									<div
										style={{ display: "flex", alignItems: "center", gap: 10 }}>
										<span style={{ fontSize: 28 }}>{m.icon}</span>
										<div>
											<div
												style={{
													fontFamily: "Syne,sans-serif",
													fontWeight: 800,
													fontSize: 15,
												}}>
												{m.label}
											</div>
										</div>
									</div>
									<Toggle on={m.active} onChange={() => {}} />
								</div>
								<div
									style={{
										borderTop: "1px solid var(--border)",
										paddingTop: 12,
									}}>
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											marginBottom: 4,
										}}>
										<span style={{ fontSize: 12, color: "var(--muted)" }}>
											Today's Revenue
										</span>
										<span
											style={{ fontSize: 13, fontWeight: 700, color: m.color }}>
											{fmt(md.amount || 0)}
										</span>
									</div>
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											marginBottom: 8,
										}}>
										<span style={{ fontSize: 12, color: "var(--muted)" }}>
											Transactions
										</span>
										<span style={{ fontSize: 13, fontWeight: 700 }}>
											{md.count || 0}
										</span>
									</div>
									<div
										style={{
											height: 4,
											background: "var(--border2)",
											borderRadius: 99,
										}}>
										<div
											style={{
												height: "100%",
												width: `${share}%`,
												background: m.color,
												borderRadius: 99,
											}}
										/>
									</div>
									<div
										style={{
											fontSize: 10,
											color: "var(--muted)",
											marginTop: 3,
										}}>
										{share}% of today's revenue
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</div>

			<div className="card">
				<div className="card-title">🏛️ Tax & Currency Configuration</div>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))",
						gap: 10,
					}}>
					{[
						["VAT Rate", "15%", "Applied to all taxable orders"],
						["Currency", "KWD — Kuwaiti Dinar", "3 decimal places standard"],
						["Rounding", "0.001 (3 decimals)", "KWD standard precision"],
						["Tax Regime", "Kuwait VAT 2021", "Mandatory collection"],
					].map(([l, v, n]) => (
						<div
							key={l}
							style={{
								background: "var(--card2)",
								borderRadius: "var(--rs)",
								padding: 14,
								border: "1px solid var(--border)",
							}}>
							<div
								style={{
									fontSize: 11,
									color: "var(--muted)",
									fontWeight: 600,
									marginBottom: 5,
									textTransform: "uppercase",
									letterSpacing: ".4px",
								}}>
								{l}
							</div>
							<div
								style={{
									fontFamily: "Syne,sans-serif",
									fontSize: 16,
									fontWeight: 800,
								}}>
								{v}
							</div>
							<div
								style={{ fontSize: 11, color: "var(--muted)", marginTop: 3 }}>
								{n}
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};
